import type { RegisteredComponent } from "@builder.io/sdk-react";
import { isEditing, isPreviewing } from "@builder.io/sdk-react";

import { SLIDER_TILE_DISPLAY } from "~/hec/components/organisms/dynamic-product-tile/display-defaults";
import { FeaturedProductSlider } from "~/hec/components/organisms/featured-product-slider/featured-product-slider";
import {
  resolveTileProductId,
  withoutUndefined,
} from "~/hec/dynamic-product-tile/dynamic-product-tile.logic";

import { featuredProductSliderInputs } from "./inputs";
import type { BuilderFeaturedProductSliderProps } from "./types";

/**
 * POC — three featured products (first 3 in stock of a priority list) that
 * share one set of tile options (CM Enhanced Content). Thin wrapper: resolves
 * each row's product id and hands the organism one `tileOptions` object;
 * spreads `attributes` on the root (`noWrap` contract, CLAUDE.md §4).
 */

const BuilderFeaturedProductSlider = ({
  headline,
  products = [],
  tileOptions = {},
  background,
  logo,
  logoAlt,
  logoHref,
  listName,
  attributes,
}: BuilderFeaturedProductSliderProps) => {
  const { className, ...rest } = attributes ?? {};
  return (
    <FeaturedProductSlider
      headline={headline}
      items={products.map(row => ({
        productId: resolveTileProductId(row.productId),
        greatFor: row.greatFor,
        keyBenefits: row.keyBenefits,
        imageSrc: row.image,
      }))}
      // Defaults applied HERE: a key missing from saved content (older entry,
      // "Reset field") takes the same value the editor showed.
      tileOptions={{
        ...SLIDER_TILE_DISPLAY,
        ...withoutUndefined(tileOptions),
      }}
      background={background}
      logoSrc={logo}
      logoAlt={logoAlt}
      logoHref={logoHref}
      listName={listName}
      inEditor={isEditing() || isPreviewing()}
      className={className}
      {...rest}
    />
  );
};

export const FeaturedProductSliderDefinition = {
  component: BuilderFeaturedProductSlider,
  name: "FeaturedProductSlider",
  // Label only — safe to rename. `name` above is the published contract.
  friendlyName: "Featured product slider",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/gallery-horizontal.svg",
  inputs: featuredProductSliderInputs,
} satisfies RegisteredComponent;

export default BuilderFeaturedProductSlider;
